import { SERVER_URL } from '../../../config';

const joinParts = (parts) => parts.filter(Boolean).join(' — ');

const isDevEnvironment = () => process.env.NODE_ENV !== 'production';

const createSendResult = ({
  success,
  status = null,
  trackerId = null,
  message = null,
}) => ({
  success,
  status,
  trackerId,
  message,
});

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseResendCheckResponse = (data) => {
  if (!data || typeof data !== 'object' || !data.success) {
    return false;
  }

  return data.needsConfirmation === true;
};

export const checkLuftResend = async ({ cycleId, notify }) => {
  try {
    const response = await fetch(
      `${SERVER_URL}/orders/generate/luft/${Number(cycleId)}/resend-check`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    const data = await response.json().catch(() => ({}));

    if (response.status === 401) {
      notify('Sessão inválida ou sem permissão para esta operação.', {
        type: 'warning',
      });
      return { canProceed: false, shouldConfirm: false };
    }

    if (!response.ok) {
      notify(
        data.userMessage
          || data.message
          || 'Não foi possível verificar se este ciclo já foi enviado anteriormente.',
        { type: 'warning' }
      );
      return { canProceed: false, shouldConfirm: false };
    }

    return {
      canProceed: true,
      shouldConfirm: parseResendCheckResponse(data),
    };
  } catch (error) {
    if (isDevEnvironment()) {
      console.error('LUFT resend-check error:', error);
    }

    notify(`Erro: ${error.message}`, { type: 'warning' });
    return { canProceed: false, shouldConfirm: false };
  }
};

const sendLuftExport = async ({ cycleId, notify }) => {
  try {
    const response = await fetch(`${SERVER_URL}/orders/generate/luft`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ id: Number(cycleId) }),
    });

    const data = await response.json().catch(() => ({}));
    const trackerId = data.trackerId;
    if (trackerId && isDevEnvironment()) {
      console.log('LUFT trackerId:', trackerId);
    }

    const notifyError = (parts) => {
      const message = joinParts([
        ...parts,
        trackerId ? `(tracker: ${trackerId})` : null,
      ]);
      notify(message, { type: 'warning' });
    };

    if (response.status === 200 && data.success) {
      const message = joinParts([
        data.userMessage || 'Informações enviadas ao parceiro logístico com sucesso.',
        data.s3 && data.s3.userMessage ? `S3: ${data.s3.userMessage}` : null,
        data.sftp && data.sftp.userMessage ? `SFTP: ${data.sftp.userMessage}` : null,
      ]);
      notify(message, { type: 'success' });
      return createSendResult({
        success: true,
        status: response.status,
        trackerId,
        message,
      });
    }

    switch (response.status) {
      case 422: {
        const errors = Array.isArray(data.errors) ? data.errors : [];
        const header = data.userMessage || data.message || 'Falha de validação ao gerar export LUFT.';
        const message = errors.length ? `${header}: ${errors.join('; ')}` : header;
        notifyError([message]);
        return createSendResult({
          success: false,
          status: response.status,
          trackerId,
          message,
        });
      }
      case 503: {
        const message = joinParts([
          data.userMessage || 'Falha no envio SFTP ao parceiro logístico.',
          data.remotePath ? `Caminho remoto: ${data.remotePath}` : null,
          data.causeMessage || data.message || null,
        ]);
        notifyError([message]);
        return createSendResult({
          success: false,
          status: response.status,
          trackerId,
          message,
        });
      }
      case 502: {
        const message = joinParts([
          data.userMessage || 'ZIP gerado, mas o envio para S3 falhou.',
          data.causeMessage || data.message || null,
        ]);
        notifyError([message]);
        return createSendResult({
          success: false,
          status: response.status,
          trackerId,
          message,
        });
      }
      case 404: {
        const message = data.userMessage || data.message || 'Ciclo inválido ou sem pedidos.';
        notifyError([message]);
        return createSendResult({
          success: false,
          status: response.status,
          trackerId,
          message,
        });
      }
      case 401: {
        const message = 'Sessão inválida ou sem permissão para esta operação.';
        notify('Sessão inválida ou sem permissão para esta operação.', {
          type: 'warning',
        });
        return createSendResult({
          success: false,
          status: response.status,
          trackerId,
          message,
        });
      }
      default: {
        const message =
          data.userMessage
            || data.message
            || `Falha ao enviar dados ao parceiro logístico (HTTP ${response.status}).`;
        notifyError([message]);
        return createSendResult({
          success: false,
          status: response.status,
          trackerId,
          message,
        });
      }
    }
  } catch (error) {
    if (isDevEnvironment()) {
      console.error('LUFT export error:', error);
    }
    const message = `Erro: ${error.message}`;
    notify(message, { type: 'warning' });
    return createSendResult({
      success: false,
      message,
    });
  }
};

export default sendLuftExport;
